import { Feather } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import { ArrowBackHeader, Button, DropdownButton, TimePicker, WebWrapper } from '_/components';
import CustomTextFieldModal from '_/components/ConfirmReserveModal/CustomTextFieldModal';
import { CustomModalType } from '_/components/ConfirmReserveModal/interface';
import CostCentersOptions from '_/components/CostCentersOptions';
import QuestionsField from '_/components/QuestionsField';
import { fieldsToCheck } from '_/components/QuestionsField/utils';
import { DATE_FORMAT, TIME_FORMAT } from '_/config/date';
import { colors, fontFamily } from '_/constants/theme';
import { formatDate } from '_/helpers/formatDate';
import { useAuth } from '_/hooks/AuthContext';
import { useDateContext } from '_/hooks/DateContext';
import { useEventContext } from '_/hooks/EventContext';
import { useSpaceContext } from '_/hooks/SpaceContext';
import { AppRoute } from '_/navigation/types';
import logger from '_/services/logger';
import { EventDate } from '_/services/models/date.model';
import { ErrorCode } from '_/services/models/enums/error-code.enum';
import { getSpaceTypeColor, SpaceType } from '_/services/models/enums/space-type.enum';
import { add, getHours, getMinutes, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, ScrollView } from 'react-native';
import { Calendar } from 'react-native-calendars';
import { Direction } from 'react-native-calendars/src/types';
import FlashMessage, { showMessage } from 'react-native-flash-message';

import { IEditEventScreenParams } from './interfaces';
import styles from './styles';
import {
  calendarTheme,
  customDisabledDates,
  selectedCustomMark,
  todayCustomMark,
} from '../DatePicker/styles';
import { locale } from '../Meeting/WeekCalendar/addLocale';
import { durationFormatter } from '_/helpers/converterMinutesInTime';
import { Question } from '_/services/models/forms.model';
import { formsApi } from '_/services/api';
import { WEEK_DAY_I18N_KEY, WeekDay } from '_/services/models/enums/week-day.enum';

interface QuotaErrorData {
  periodType?: string;
  limitMinutes?: number;
  requestedMinutes?: number;
  remainingMinutes?: number;
}

const EditEvents: React.FC = () => {
  const { today, maxDate, checkOpenCloseHourMin } = useDateContext();
  const route = useRoute();
  const { event } = route.params as IEditEventScreenParams;
  const { slot } = event;
  const { space } = slot || {};
  const startAtToDateType = parseISO(event.startAt);
  const endAtToDateType = parseISO(event.endAt);
  const { disabledDateData } = useSpaceContext();
  const { updateEvent, getTodayEvents, modalMessage } = useEventContext();
  const navigation = useNavigation<any>();
  const { t } = useTranslation();
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [markedDates, setMarkedDates] = useState<Record<string, any>>({});
  const [selectedNewTime, setSelectedNewTime] = useState<EventDate>({
    start: startAtToDateType,
    end: endAtToDateType,
  });
  const { member } = useAuth();
  const showCustomFields = Boolean(member?.showCustomFields);
  const [form, setForm] = useState<Question[]>([]);
  const [customModalVisible, setCustomModalVisible] = useState<CustomModalType>();

  const [costCenter, setCostCenter] = useState<string>('');
  const [newDescription, setNewDescription] = useState(event.description);

  const {
    isIntervalInvalid,
    hasStartTimeBeforeCurrent,
    setSelectedDay,
    selectedEventDate,
    setSelectedEventDate,
    setUpdateEventDate,
  } = useDateContext();

  const selectedDay = useMemo(
    () => formatDate(selectedNewTime.start, DATE_FORMAT),
    [selectedNewTime]
  );

  const handleSetSelectedDay = (day: string) => {
    setSelectedDay(day);

    setSelectedNewTime((prevState) => ({
      start: add(parseISO(day), {
        hours: getHours(prevState.start),
        minutes: getMinutes(prevState.start),
      }),
      end: add(parseISO(day), {
        hours: getHours(prevState.end),
        minutes: getMinutes(prevState.end),
      }),
    }));
  };

  const handleEditEvent = async () => {
    const startAt = add(parseISO(selectedDay), {
      hours: selectedNewTime.start.getHours(),
      minutes: selectedNewTime.start.getMinutes(),
    });

    const endAt = add(parseISO(selectedDay), {
      hours: selectedNewTime.end.getHours(),
      minutes: selectedNewTime.end.getMinutes(),
    });

    if (checkOpenCloseHourMin(startAt, endAt, space?.openAtHourMin, space?.closeAtHourMin)) {
      showMessage({
        message: t('error'),
        description: t('workstationScreen.errorMessages.checkOpenCloseHourMinError', {
          openAt: space?.openAtHourMin ?? '00:00',
          closeAt: space?.closeAtHourMin ?? '23:59',
        }),
        backgroundColor: colors.errorRed,
      });
      return;
    }

    const showError = fieldsToCheck({
      form,
      showCustomFields,
    });

    if (showError) {
      return modalMessage.current.showMessage({
        message: t('error'),
        description: t('workstationScreen.errorMessages.quizRequired'),
        backgroundColor: colors.errorRed,
      });
    }

    setRequestIsLoading(true);
    const newData = {
      ...(newDescription && { description: newDescription }),
      questions: form,
      startAt,
      endAt,
      costCenter: costCenter,
    };

    try {
      await updateEvent(event.id, newData);
      showMessage({
        message: t('success'),
        description: t('editEventScreen.successMessage'),
        backgroundColor: colors.successGreen,
      });
      setRequestIsLoading(false);
      navigation.navigate(AppRoute.HOME);
      await getTodayEvents();
    } catch (error: any) {
      let errorMessage = t('workstationScreen.errorMessages.reservationFail');
      let message = t('error');
      let autoHide = true;
      if (error?.response?.data?.data?.code === ErrorCode.HAS_CONFLICT) {
        errorMessage = t('workstationScreen.errorMessages.scheduleConflict');
      } else if (error?.response?.data?.data?.code === ErrorCode.HAS_USER_CONFLICT) {
        errorMessage = t('workstationScreen.errorMessages.alreadyScheduled');
      } else if (error?.response?.data?.data?.code === ErrorCode.NO_PAST_EVENT_SCHEDULE) {
        errorMessage = t('workstationScreen.errorMessages.noPastReservations');
      } else if (error?.response?.data?.data?.code === ErrorCode.HAS_MAX_CAPACITY_REACHED) {
        errorMessage = t('workstationScreen.noAvailableSlots.maxCapacityReached');
      } else if (error?.response?.data?.data?.code === ErrorCode.OPEN_CLOSE_SPACE_ERROR) {
        errorMessage = t('workstationScreen.errorMessages.checkOpenCloseHourMinError', {
          openAt: space?.openAtHourMin ?? '00:00',
          closeAt: space?.closeAtHourMin ?? '23:59',
        });
      } else if (error?.response?.data?.data?.code === ErrorCode.ONLY_ADMIN) {
        errorMessage = t('workstationScreen.errorMessages.onlyAdmin');
      } else if (error?.response?.data?.data?.code === ErrorCode.MAX_EVENT_DURATION_IN_MINUTES) {
        errorMessage = t('workstationScreen.errorMessages.maxEventDuration', {
          maxDuration: durationFormatter(slot?.maxEventDurationInMinutes),
        });
      } else if (error?.response?.data?.data?.code === ErrorCode.WEEK_DAY_NOT_ALLOWED) {
        errorMessage = t('workstationScreen.errorMessages.weekDaysNotAllowed', {
          weekDays: error?.response?.data?.data?.allowedWeekDays
            .map((weekDay: WeekDay) => t(WEEK_DAY_I18N_KEY[weekDay]))
            .join(', '),
        });
      } else if (error?.response?.data?.data?.code === ErrorCode.RESERVATION_QUOTA_EXCEEDED) {
        autoHide = false;
        const errorData = error?.response?.data?.data?.data as QuotaErrorData;
        if (errorData) {
          message =
            errorData.periodType === 'DAILY'
              ? t('workstationScreen.errorMessages.quotaDailyExceeded')
              : t('workstationScreen.errorMessages.quotaMonthlyExceeded');
          errorMessage = t('workstationScreen.errorMessages.reservationQuotaExceeded', {
            requested: durationFormatter(errorData.requestedMinutes),
            remaining: durationFormatter(errorData.remainingMinutes),
            limit: durationFormatter(errorData.limitMinutes),
          });
        }
      } else {
        errorMessage = t('editEventScreen.errorMessage');
      }
      setRequestIsLoading(false);
      showMessage({
        message,
        description: errorMessage,
        backgroundColor: colors.errorRed,
        autoHide,
      });
      logger(error);
    }
  };

  const selectedStartTime = useMemo(() => {
    return formatDate(selectedNewTime.start, TIME_FORMAT, { locale });
  }, [selectedNewTime]);

  const selectedEndTime = useMemo(() => {
    return formatDate(selectedNewTime.end, TIME_FORMAT, { locale });
  }, [selectedNewTime]);

  useEffect(() => {
    async function getForm() {
      const res = await formsApi.getItem(slot?.formId as string);
      const questions = res.questions ?? ([] as Question[]);

      if (!questions.length) return setForm([]);
      if (!event.questions?.length) return setForm(questions);
      const updatedQuestions = questions.map((question) => {
        const matchedQuestion = event.questions?.find((q) => {
          if (q.id && question.id) return q.id === question.id;
          return q.label === question.label;
        });
        return matchedQuestion ? { ...question, value: matchedQuestion.value } : question;
      });
      setForm(updatedQuestions);
    }

    if (slot?.formId) getForm();
  }, [slot?.formId]);

  useEffect(() => {
    const eventDateStart = selectedEventDate.start.toString();
    const eventDateEnd = selectedEventDate.end.toString();
    const eventNewTimeStart = selectedNewTime.start.toString();
    const eventNewTimeEnd = selectedNewTime.end.toString();

    if (eventDateStart !== eventNewTimeStart || eventDateEnd !== eventNewTimeEnd) {
      setSelectedEventDate({
        start: selectedNewTime.start,
        end: selectedNewTime.end,
      });
    }
  }, [selectedNewTime]);

  useEffect(() => {
    const marks: Record<string, any> = {
      [formatDate(today, DATE_FORMAT)]: { selected: true, customStyles: todayCustomMark },
      [selectedDay]: {
        selected: true,
        customStyles: selectedCustomMark(space?.type),
      },
    };

    disabledDateData?.forEach(({ date }) => {
      marks[formatDate(parseISO(date), DATE_FORMAT)] = {
        selected: true,
        customStyles: customDisabledDates,
      };
    });

    setMarkedDates(marks);
  }, [disabledDateData, space?.type, selectedDay, today]);

  useEffect(() => {
    setUpdateEventDate({ start: new Date(event.startAt), end: new Date(event.endAt) });
    return () => {
      setUpdateEventDate(undefined);
    };
  }, []);

  return (
    <ScrollView style={{ flex: 1 }}>
      <WebWrapper>
        <FlashMessage
          floating
          position="bottom"
          duration={2000}
          titleStyle={{ fontFamily: fontFamily.bold }}
          textStyle={{ fontFamily: fontFamily.regular }}
          ref={modalMessage}
        />
        <View style={styles.wrapper}>
          <ArrowBackHeader style={{ marginBottom: 32 }}>
            {t('editEventScreen.editEvent')}
          </ArrowBackHeader>
          <DropdownButton
            text={newDescription || t('editEventScreen.newTitle')}
            icon="edit"
            tag={event.description && t('editEventScreen.newTitle')}
            onPress={() => setCustomModalVisible('title')}
          />
          <CostCentersOptions
            locationId={space?.location?.id as string}
            slotCostCenterEnabled={Boolean(slot?.costCenterEnabled)}
            memberShowCustomFields={Boolean(showCustomFields)}
            costCenter={costCenter}
            setCostCenter={setCostCenter}
            customModalVisible={customModalVisible}
            setCustomModalVisible={setCustomModalVisible}
            spaceType={space?.type as SpaceType}
          />
          <QuestionsField form={form} setForm={setForm} memberShowCustomFields={showCustomFields} />
          <Calendar
            theme={calendarTheme(space?.type)}
            minDate={today?.toISOString()}
            maxDate={maxDate?.toISOString()}
            onDayPress={(day) => handleSetSelectedDay(day.dateString)}
            markingType="custom"
            markedDates={markedDates}
            renderArrow={(direction: Direction) => (
              <Feather name={`chevron-${direction}` as any} size={28} color={colors.darkGrey} />
            )}
          />
        </View>
        <TimePicker
          editable
          defaultSelectedStartTime={selectedStartTime}
          defaultSelectedEndTime={selectedEndTime}
          setEventDate={setSelectedNewTime}
          selectedNewTime={selectedNewTime}
        />
        <View style={styles.buttonWrapper}>
          <View
            style={{
              opacity: !selectedDay || isIntervalInvalid || hasStartTimeBeforeCurrent ? 0.2 : 1,
            }}
          >
            <Button
              backgroundColor={getSpaceTypeColor(space?.type)}
              textColor={colors.white}
              onPress={handleEditEvent}
              disabled={isIntervalInvalid || hasStartTimeBeforeCurrent}
              loading={requestIsLoading}
            >
              {t('editEventScreen.editEvent')}
            </Button>
          </View>
        </View>
        <CustomTextFieldModal
          spaceType={space?.type as SpaceType}
          hideDropdown={() => setCustomModalVisible(undefined)}
          label={t('editEventScreen.newTitle')}
          setTextFieldValue={setNewDescription}
          value={newDescription}
          visible={customModalVisible === 'title'}
        />
      </WebWrapper>
    </ScrollView>
  );
};

export default EditEvents;
